import React from 'react';
import { Drawer } from 'antd';
import {
    Table,
    Input,
    InputNumber,
    Label,
    Select,
    Button,
    IconButton,
    Text,
    ButtonHierarchies,
    EmptyState,
    InputSizes,
    SelectSizes,
} from '@clockout-dev/design-elements';
import { CloseOutlined } from '@ant-design/icons';
import MemberIdEditor from './MemberIdEditor';
import CsvImportButton from './CsvImportButton';
import { PROMO_WINDOW_UNIT_OPTIONS, memberColumns } from './freeAdvances.constants';
import styles from './styles/freeAdvances.module.scss';

/**
 * Create/edit a promo group. A new group has no `promo_id` for members to
 * attach to yet, so everything (name, rule, enabled, member roster) is
 * staged locally and committed in one Save — which also keeps this fully
 * isolated from the Fee Structure tab's own Save button; the two never
 * touch the same slice.
 *
 * Laid out as two sections — Group (what the promo is) and Members (who gets
 * it) — because those are the two independent things a reader is here to
 * change; the roster is usually much longer than the rule, and without a
 * header between them the CSV import reads as if it belonged to the rule.
 */
function PromoGroupDrawer({
    drawer = {},
    canManage = false,
    onClose,
    onFieldChange,
    onAddMember,
    onImportMembersCsv,
    onRemoveMember,
    onSave,
}) {
    const {
        open = false,
        mode = 'create',
        draft = {},
        members = [],
        loading = false,
        saving = false,
        error = null,
    } = drawer;

    const columns = canManage
        ? [
              ...memberColumns,
              {
                  title: '',
                  key: 'actions',
                  render: (_, row) => (
                      <IconButton
                          type="button"
                          icon={<CloseOutlined />}
                          aria-label={`Remove ${row.member_id}`}
                          onClick={() => onRemoveMember(row.member_id)}
                      />
                  ),
              },
          ]
        : memberColumns;

    return (
        <Drawer
            open={open}
            onClose={onClose}
            placement="right"
            width={640}
            destroyOnHidden
            rootClassName={styles.drawer}
            title={mode === 'create' ? 'Add new group' : `Edit ${draft.label || 'group'}`}
        >
            <div className={styles.drawerBody}>
                <Text as="span" size="s" className={styles.sectionDescription}>
                    The promo window starts when the member first enters the platform, or when they&apos;re added to
                    this group if they already had access — whichever happens later.
                </Text>

                <section className={styles.drawerSection}>
                    <div className={styles.drawerSectionHeader}>
                        <div className={styles.drawerSectionHeading}>
                            <Text as="span" size="s" className={styles.drawerSectionTitle}>
                                Group
                            </Text>
                        </div>
                    </div>

                    <div className={styles.drawerSectionBody}>
                        <Input
                            size={InputSizes.SMALL}
                            label="Group name"
                            value={draft.label || ''}
                            onChange={(e) => onFieldChange('label', e.target.value)}
                            disabled={!canManage}
                        />

                        <Label label="Rule">
                            <div className={styles.drawerRuleRow}>
                                <Text as="span" size="s" className={styles.drawerRuleLabel}>
                                    First
                                </Text>
                                <InputNumber
                                    size={InputSizes.SMALL}
                                    value={draft.free_advance_count}
                                    onChange={(value) => onFieldChange('free_advance_count', value)}
                                    min={1}
                                    max={999}
                                    controls={false}
                                    disabled={!canManage}
                                    className={styles.drawerNumberInput}
                                />
                                <Text as="span" size="s" className={styles.drawerRuleLabel}>
                                    advances are free at first
                                </Text>
                                <InputNumber
                                    size={InputSizes.SMALL}
                                    value={draft.window_value}
                                    onChange={(value) => onFieldChange('window_value', value)}
                                    min={1}
                                    max={999}
                                    controls={false}
                                    disabled={!canManage}
                                    className={styles.drawerNumberInput}
                                />
                                <Select
                                    size={SelectSizes.SMALL}
                                    value={draft.window_unit}
                                    options={PROMO_WINDOW_UNIT_OPTIONS}
                                    onChange={(value) => onFieldChange('window_unit', value)}
                                    disabled={!canManage}
                                    className={styles.drawerSelect}
                                />
                            </div>
                        </Label>
                    </div>
                </section>

                <section className={styles.drawerSection}>
                    <div className={styles.drawerSectionHeader}>
                        <div className={styles.drawerSectionHeading}>
                            <Text as="span" size="s" className={styles.drawerSectionTitle}>
                                Members
                            </Text>
                            {members.length > 0 ? (
                                <span className={styles.drawerSectionCount}>{members.length}</span>
                            ) : null}
                        </div>
                        {canManage ? <CsvImportButton onImport={onImportMembersCsv} disabled={saving} /> : null}
                    </div>

                    <div className={styles.drawerSectionBody}>
                        {canManage ? <MemberIdEditor onAdd={onAddMember} disabled={saving} /> : null}
                        {members.length > 0 ? (
                            <div className={styles.tableScroll}>
                                <Table
                                    columns={columns}
                                    dataSource={members}
                                    loading={loading}
                                    rowKey="member_id"
                                    pagination={false}
                                    emptyState={<EmptyState title="No members yet." />}
                                />
                            </div>
                        ) : (
                            <Text as="span" size="s" className={styles.membersEmpty}>
                                No members added yet.
                            </Text>
                        )}
                    </div>
                </section>

                {error ? (
                    <Text as="span" size="s" className={styles.errorText}>
                        {error}
                    </Text>
                ) : null}
            </div>

            {canManage ? (
                <div className={styles.footer}>
                    <Button
                        type="button"
                        hierarchy={ButtonHierarchies.SECONDARY}
                        className={styles.footerButton}
                        onClick={onClose}
                        disabled={saving}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        className={styles.footerButton}
                        onClick={onSave}
                        disabled={saving || !draft.label?.trim()}
                    >
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </div>
            ) : null}
        </Drawer>
    );
}

export default PromoGroupDrawer;
